import React, { useState, useMemo } from "react";

import { apiService } from '../services/apiService';

const Dashboard = ({ forms: initialForms, onCreate, onEdit, onDelete, onOpen, onViewReport }) => {
  // --- STATE ---
  // NEW
  const [localForms, setLocalForms] = useState(initialForms.map(f => ({
    ...f,
    // Use the status from DB. Only default to 'Draft' if DB status is missing.
    status: f.status || 'Draft'
  })));

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [sortBy, setSortBy] = useState("Newest First");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // --- STYLES CONSTANTS (From your original code) ---
  const fontStyleMain = { fontSize: "16px" };
  const fontStyleHeading = { fontSize: "23px", fontWeight: 800 };

  // --- ACTIONS ---
  const handleStatusChange = async (e, formId) => {
    e.stopPropagation();
    const newStatus = e.target.value;

    // 1. Optimistic Update: Update UI immediately so it feels fast
    setLocalForms(prevForms => prevForms.map(f =>
      f.id === formId ? { ...f, status: newStatus } : f
    ));

    // 2. API Call: Save to database
    try {
      await apiService.updateForm(formId, { status: newStatus });
      console.log("Status saved successfully");
    } catch (error) {
      console.error("Failed to save status:", error);
      // Optional: Revert change if API fails
      alert("Failed to update status. Please try again.");
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedFormId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (selectedFormId) onDelete(selectedFormId);
    setLocalForms(prev => prev.filter(f => f.id !== selectedFormId));
    setShowDeleteModal(false);
    setSelectedFormId(null);
  };

  const handleCopyLink = (e, formId) => {
    e.stopPropagation();
    const link = `${window.location.origin}/form/${formId}`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => setShowCopyModal(true))
        .catch(err => console.error('Failed to copy', err));
    } else {
      // Fallback for non-secure contexts (HTTP)
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "fixed"; // Avoid scrolling to bottom
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) setShowCopyModal(true);
        else console.error('Fallback: Copy command was unsuccessful');
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
      }

      document.body.removeChild(textArea);
    }
  };

  // --- STATS CALCULATIONS ---
  const totalForms = localForms.length;
  const totalResponses = localForms.reduce((acc, form) => acc + (Number(form.responses) || 0), 0);
  const activeSurveys = localForms.filter(f => f.status === 'Active').length;
  const completionRate = totalForms > 0 ? ((activeSurveys / totalForms) * 100).toFixed(1) : 0;

  // --- FILTERING & SORTING ---
  const processedForms = useMemo(() => {
    let result = [...localForms];

    // 1. Search
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(f =>
        f.title.toLowerCase().includes(lower) ||
        (f.description && f.description.toLowerCase().includes(lower))
      );
    }

    // 2. Status Filter
    if (statusFilter !== "All Status") {
      result = result.filter(f => f.status === statusFilter);
    }

    // 3. Sort
    result.sort((a, b) => {
      const dateA = new Date(a.updated_date || a.created_date || 0);
      const dateB = new Date(b.updated_date || b.created_date || 0);
      if (sortBy === "Newest First") return dateB - dateA;
      if (sortBy === "Oldest First") return dateA - dateB;
      return 0;
    });

    return result;
  }, [localForms, searchTerm, statusFilter, sortBy]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentForms = processedForms.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(processedForms.length / itemsPerPage);

  // --- HELPERS ---
  const formatTime = (dateString) => {
    if (!dateString) return { relative: "Just now", absolute: "" };
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    const absolute = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    let relative = "Just now";
    let interval = seconds / 31536000;
    if (interval > 1) { relative = Math.floor(interval) + " years ago"; }
    else {
      interval = seconds / 2592000;
      if (interval > 1) { relative = Math.floor(interval) + " months ago"; }
      else {
        interval = seconds / 86400;
        if (interval > 1) { relative = Math.floor(interval) + " days ago"; }
        else {
          interval = seconds / 3600;
          if (interval > 1) { relative = Math.floor(interval) + " hours ago"; }
          else {
            interval = seconds / 60;
            if (interval > 1) { relative = Math.floor(interval) + " mins ago"; }
          }
        }
      }
    }
    return { relative, absolute };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return { bg: '#d1fae5', text: '#059669', dot: '#10b981' };
      case 'Draft': return { bg: '#fef3c7', text: '#d97706', dot: '#f59e0b' };
      case 'Closed': return { bg: '#f1f5f9', text: '#64748b', dot: '#94a3b8' };
      default: return { bg: '#f1f5f9', text: '#64748b', dot: '#94a3b8' };
    }
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f9fafb' }}>
      {/* Bootstrap Icons */}
      <link href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css" rel="stylesheet" />

      <style>{`
        body { background-color: #f9fafb; color: #0f172a; }
         .btn { transition: all 0.2s ease-in-out; }
                .btn:hover { filter: brightness(1.1); transform: translateY(-2px); }
                .btn:active { transform: scale(0.97); }
        
        /* Stats Cards */
        .stat-card {
            background: white; border-radius: 16px; border: 1px solid #33f80c50;
            padding: 24px; transition: transform 0.2s, box-shadow 0.2s;
            height: 100%; position: relative; overflow: hidden;
        }
        @media (max-width: 767px) {
            .stat-card { padding: 12px; }
            .stat-icon { width: 32px !important; height: 32px !important; margin-bottom: 4px !important; }
            .stat-icon i { font-size: 1rem !important; }
            .stat-value { font-size: 20px !important; }
        }
        .stat-card:hover { transform: translateY(-2px);border: 1px solid #33f80ca4; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05); }
        .stat-icon { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
        
        /* Table Styles */
        .table-card { background: white; border-radius: 16px; border: 1px solid #18ff103a; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05); overflow: hidden; margin-bottom: 40px; }
        .table-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; background: white; }
        
        .custom-table { width: 100%; border-collapse: collapse; }
        .custom-table th {
            text-align: left; padding: 16px 24px; font-size: 12px; font-weight: 600;
            text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; background-color: #fcfcfc;
            border-bottom: 1px solid #e2e8f0;
        }
        .custom-table td { padding: 16px 24px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; color: #334155; }
        .custom-table tr:hover td { background-color: #f8fafc; }
        .custom-table tr:last-child td { border-bottom: none; }
        
        /* Inputs & Dropdowns */
        .search-input {
            background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
            padding: 8px 12px 8px 36px; font-size: 14px; width: 100%; outline: none; transition: all 0.2s;
        }
        @media (min-width: 768px) {
            .search-input { width: 220px; }
        }
        .search-input:focus { background: white; border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
        
        .filter-select {
            background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
            padding: 8px 32px 8px 12px; font-size: 14px; cursor: pointer; appearance: none; outline: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
            background-repeat: no-repeat; background-position: right 8px center; background-size: 14px;
        }
        
        /* Status Badge Select */
        .status-select {
            appearance: none; border: none; font-size: 11px; font-weight: 700; text-transform: uppercase;
            padding: 4px 24px 4px 12px; border-radius: 6px; cursor: pointer; outline: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='3'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
            background-repeat: no-repeat; background-position: right 6px center; background-size: 10px;
        }

        /* Action Buttons Hover Effect */
        .action-btn { transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
        .action-btn:hover { transform: scale(1.15); filter: brightness(0.9); }

        .progress-track { background: #f1f5f9; height: 4px; border-radius: 99px; width: 60px; overflow: hidden; margin-left: auto; }
        
        .modal-backdrop.show { opacity: 0.5; }
      `}</style>

      <div className="container-fluid px-4 px-lg-5 py-5">

        {/* --- 1. HEADER --- */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center text-center text-md-start mb-5 gap-3">
          <div>
            <h1 className=" mb-1 " style={fontStyleHeading}>Dashboard</h1>
            <p className="text-muted m-0" style={fontStyleMain}>Easily manage your forms and data in one place.</p>
          </div>
          <button
            className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 rounded-3 shadow-sm border-0 mx-auto mx-md-0"
            onClick={onCreate}
            style={{ fontSize: '14px', fontWeight: '600' }}
          >
            <i className="bi-plus-circle-fill me-0"></i> New Form
          </button>
        </div>

        {/* --- 2. STATS GRID --- */}
        <div className="row g-2 g-md-4 mb-4 mb-md-5">
          {/* Card 1: Total Forms */}
          <div className="col-6 col-md-6 col-xl-3">
            <div className="stat-card">
              <div className="d-flex justify-content-between align-items-start mb-1 mb-md-3">
                <div className="stat-icon" style={{ backgroundColor: '#e0e7ff', color: '#4f46e5' }}>
                  <i className="bi bi-file-earmark-text fs-5"></i>
                </div>

              </div>
              <h3 className="text-muted text-uppercase fw-bold mb-0 mb-md-1" style={{ fontSize: '10px', letterSpacing: '0.05em' }}>Total Forms</h3>
              <p className="mb-0 text-dark stat-value" style={fontStyleHeading}>{totalForms}</p>
            </div>
          </div>

          {/* Card 2: Total Responses */}
          <div className="col-6 col-md-6 col-xl-3">
            <div className="stat-card">
              <div className="d-flex justify-content-between align-items-start mb-1 mb-md-3">
                <div className="stat-icon" style={{ backgroundColor: '#d1fae5', color: '#059669' }}>
                  <i className="bi bi-chat-dots fs-5"></i>
                </div>
                <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-2 py-1" style={{ fontSize: '10px' }}></span>
              </div>
              <h3 className="text-muted text-uppercase fw-bold mb-0 mb-md-1" style={{ fontSize: '10px', letterSpacing: '0.05em' }}>Total Responses</h3>
              <p className="mb-0 text-dark stat-value" style={fontStyleHeading}>{totalResponses.toLocaleString()}</p>
            </div>
          </div>

          {/* Card 3: Active Surveys */}
          <div className="col-6 col-md-6 col-xl-3">
            <div className="stat-card">
              <div className="d-flex justify-content-between align-items-start mb-1 mb-md-3">
                <div className="stat-icon" style={{ backgroundColor: '#fef3c7', color: '#d97706' }}>
                  <i className="bi bi-lightning-charge fs-5"></i>
                </div>
                <span className="badge bg-warning bg-opacity-10 text-warning rounded-pill px-2 py-1" style={{ fontSize: '10px' }}>Live</span>
              </div>
              <h3 className="text-muted text-uppercase fw-bold mb-0 mb-md-1" style={{ fontSize: '10px', letterSpacing: '0.05em' }}>Active Surveys</h3>
              <p className="mb-0 text-dark stat-value" style={fontStyleHeading}>{activeSurveys}</p>
            </div>
          </div>

          {/* Card 4: Completion Rate */}
          <div className="col-6 col-md-6 col-xl-3">
            <div className="stat-card">
              <div className="d-flex justify-content-between align-items-start mb-1 mb-md-3">
                <div className="stat-icon" style={{ backgroundColor: '#e0e7ff', color: '#4f46e5' }}>
                  <i className="bi bi-check2-square fs-5"></i>
                </div>
                <span className="badge bg-secondary bg-opacity-10 text-secondary rounded-pill px-2 py-1" style={{ fontSize: '10px' }}>Avg</span>
              </div>
              <h3 className="text-muted text-uppercase fw-bold mb-0 mb-md-1" style={{ fontSize: '10px', letterSpacing: '0.05em' }}>Completion Rate</h3>
              <p className="mb-0 text-dark stat-value" style={fontStyleHeading}>{completionRate}%</p>
            </div>
          </div>
        </div>

        {/* --- 3. RECENT FORMS TABLE --- */}
        <div className="table-card">
          <div className="table-header d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
            <h2 className="m-0 text-slate-900" style={fontStyleHeading}>Recent Forms</h2>

            <div className="row g-2 align-items-center">
              {/* Search */}
              <div className="col-12 col-md-auto">
                <div className="position-relative">
                  <i className="bi bi-search position-absolute text-muted" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px' }}></i>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search forms..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="col-6 col-md-auto">
                <select className="filter-select w-100" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="All Status">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Draft">Draft</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>

              {/* Sort Filter */}
              <div className="col-6 col-md-auto">
                <select className="filter-select w-100" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="Newest First">Newest First</option>
                  <option value="Oldest First">Oldest First</option>
                </select>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>Form Name</th>
                  <th>Status</th>
                  <th>Responses</th>
                  <th>Last Modified</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentForms.length > 0 ? (
                  currentForms.map((form) => {
                    const responses = Number(form.responses) || 0;
                    const isActive = form.status === 'Active';
                    const styles = getStatusColor(form.status);
                    const progress = isActive ? Math.min(100, (responses / 20) * 100) : 0;
                    const dateInfo = formatTime(form.updated_date || form.created_date);

                    return (
                      <tr key={form.id} onClick={() => onOpen(form.id)} style={{ cursor: 'pointer' }}>
                        {/* Name */}
                        <td width="35%">
                          <div className="d-flex align-items-center gap-3">
                            <div className="stat-icon" style={{ width: '40px', height: '40px', background: styles.bg, color: styles.text }}>
                              <i className="bi bi-file-text"></i>
                            </div>
                            <div>
                              <p className="text-dark m-0" style={{ ...fontStyleHeading, fontSize: '14px' }}>{form.title}</p>
                              <p className="text-muted m-0 text-truncate" style={{ fontSize: '12px', maxWidth: '250px' }}>{form.description || "No description"}</p>
                            </div>
                          </div>
                        </td>

                        {/* Status (Editable) */}
                        <td width="15%">
                          <div className="d-inline-block position-relative">
                            <select
                              className="status-select"
                              value={form.status}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleStatusChange(e, form.id)}
                              style={{ backgroundColor: styles.bg, color: styles.text }}
                            >
                              <option value="Active">Active</option>
                              <option value="Draft">Draft</option>
                              <option value="Closed">Closed</option>
                            </select>
                          </div>
                        </td>

                        {/* Responses */}
                        <td width="20%">
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold text-dark" style={fontStyleMain}>{responses}</span>
                            <div className="progress-track" style={{ width: '60px' }}>
                              <div className="h-100" style={{ width: `${progress}%`, backgroundColor: styles.dot }}></div>
                            </div>
                          </div>
                        </td>

                        {/* Last Modified (Date included) */}
                        <td width="15%">
                          <div className="d-flex flex-column">
                            <span className="text-dark" style={fontStyleMain}>{dateInfo.relative}</span>
                            <span className="text-muted" style={{ fontSize: '11px' }}>{dateInfo.absolute}</span>
                          </div>
                        </td>

                        {/* Actions (Custom Buttons) */}
                        <td width="15%" className="text-end">
                          <div className="d-flex align-items-center gap-2 justify-content-end" onClick={(e) => e.stopPropagation()}>

                            <button className="btn action-btn d-flex align-items-center justify-content-center" onClick={(e) => handleCopyLink(e, form.id)} title="Copy Link" style={{ width: "36px", height: "36px", borderRadius: "10px", backgroundColor: "#e1f3efff", color: "#0f766e", border: "none" }}>
                              <i className="bi bi-link-45deg" style={{ fontSize: "18px" }}></i>
                            </button>

                            <button className="btn action-btn d-flex align-items-center justify-content-center" onClick={() => onViewReport(form.id)} title="View Report" style={{ width: "36px", height: "36px", borderRadius: "10px", backgroundColor: "#e3f7edff", color: "#059669", border: "none" }}>
                              <i className="bi bi-file-earmark-bar-graph" style={{ fontSize: "16px" }}></i>
                            </button>

                            <button className="btn action-btn d-flex align-items-center justify-content-center" onClick={() => onEdit(form.id)} title="Edit Form" style={{ width: "36px", height: "36px", borderRadius: "10px", backgroundColor: "#e5edf8ff", color: "#3b82f6", border: "none" }}>
                              <i className="bi bi-pencil-square" style={{ fontSize: "16px" }}></i>
                            </button>

                            <button className="btn action-btn text-danger d-flex align-items-center justify-content-center" onClick={() => handleDeleteClick(form.id)} title="Delete" style={{ width: "36px", height: "36px", borderRadius: "10px", backgroundColor: "#f7e6e6ff", fontSize: "16px", border: "none" }}>
                              <i className="bi bi-trash"></i>
                            </button>

                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5 text-muted" style={fontStyleMain}>
                      No forms found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / Pagination */}
          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between px-4 py-3 border-top gap-3">
            <span className="text-muted small">
              Showing {currentForms.length > 0 ? indexOfFirstItem + 1 : 0} to {Math.min(indexOfLastItem, processedForms.length)} of {processedForms.length} forms
            </span>

            <div className="d-flex gap-1">
              <button
                className="btn btn-sm btn-light border d-flex align-items-center justify-content-center"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(c => c - 1)}
                style={{ width: '32px', height: '32px' }}
              >
                <i className="bi bi-chevron-left" style={{ fontSize: '14px' }}></i>
              </button>

              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`btn btn-sm fw-bold ${currentPage === i + 1 ? 'btn-primary' : 'btn-light border'}`}
                  style={{ width: '32px', height: '32px', backgroundColor: currentPage === i + 1 ? '#4f46e5' : 'white', borderColor: currentPage === i + 1 ? '#4f46e5' : '#e2e8f0' }}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}

              <button
                className="btn btn-sm btn-light border d-flex align-items-center justify-content-center"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(c => c + 1)}
                style={{ width: '32px', height: '32px' }}
              >
                <i className="bi bi-chevron-right" style={{ fontSize: '14px' }}></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- MODALS --- */}
      {showDeleteModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-body text-center p-4">
                  <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex p-3 mb-3">
                    <i className="bi bi-trash3 fs-2"></i>
                  </div>
                  <h5 className="fw-bold mb-2" style={fontStyleHeading}>Delete Form?</h5>
                  <p className="text-muted small mb-4" style={fontStyleMain}>Are you sure? This action cannot be undone.</p>
                  <div className="d-flex gap-2 justify-content-center">
                    <button className="btn btn-light w-100 rounded-3" onClick={() => setShowDeleteModal(false)} style={fontStyleMain}>Cancel</button>
                    <button className="btn btn-danger w-100 rounded-3" onClick={confirmDelete} style={fontStyleMain}>Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showCopyModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-body text-center p-4">
                  <div className="bg-success bg-opacity-10 text-success rounded-circle d-inline-flex p-3 mb-3">
                    <i className="bi bi-check2 fs-2"></i>
                  </div>
                  <h5 className="fw-bold mb-2" style={fontStyleHeading}>Link Copied!</h5>
                  <p className="text-muted small mb-4" style={fontStyleMain}>The form link is ready to share.</p>
                  <button className="btn btn-primary w-100 rounded-3" onClick={() => setShowCopyModal(false)} style={fontStyleMain}>OK</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;